<template>
  <component
    class="teaser-item"
    :class="{ '-campaign': isCampaignTheme, '-ambassador': isAmbassadorTheme, '-smalltext': smallText }"
    :is="model.Link ? 'a' : 'div'"
    :href="model.Link"
    :style="{
      color: fontColor
    }"
  >
    <div class="-p-relative" :class="{ '-text-on-image': textOnImage }">
      <div
        v-if="textOnImage && loaded"
        class="overlay-image"
        :class="{ '-loading': !loaded }"
      ></div>
      <v-lazy-image
        :style="imgStyle"
        :srcset="imageObj.srcset"
        :sizes="sizes"
        :alt="imageObj.description"
        :src="imageObj.src"
        @load="loaded = true"
      />

      <div class="teaser-item-body">
        <div
          v-if="model.Label"
          class="teaser-item-label font-responsive-small -small-caps m-b-3"
          v-html="model.Label"
        ></div>
        <h3
          :class="
            !isCampaignTheme
              ? 'font-responsive-large'
              : 'font-responsive-body m-b-0 -bold'
          "
          v-html="title"
        ></h3>
        <div
          class="teaser-item-text"
          :class="{
            'font-responsive-body -thin': isCampaignTheme,
            'font-responsive-small': isAmbassadorTheme,
          }"
          v-html="model.Text"
        ></div>
        <div v-if="model.LinkText" class="teaser-item-link m-t-9">
          <span>{{ model.LinkText }}</span>
          <Arrow
            v-if="isAmbassadorTheme"
            class="m-l-6"
            :thickness="'light'"
            :direction="'right'"
            :color="Globals.colors.primary"
          />
        </div>
      </div>
    </div>
  </component>
</template>

<script>
import VLazyImage from 'v-lazy-image';
import imgSrcMixin from '@/Scripts/lib/mixins/imgSrcMixin';
import ImageCollectorHelper from '@/Scripts/lib/helper/imageCollectorHelper.js';
import Arrow from '@/Scripts/components/shared/svg/Arrow/Arrow.vue';

export default {
  name: 'TeaserItem',
  components: {
    VLazyImage,
    Arrow
  },
  mixins: [imgSrcMixin],
  props: {
    model: Object,
    textOnImage: Boolean,
    proportion: {
      type: Number,
      default: 1.5
    },
    sizes: {
      type: String,
      default: '100vw'
    },
    theme: Number,
    ambassador: Boolean,
    smallText: Boolean,
    fontColor: String
  },
  data () {
    return {
      loaded: false
    };
  },
  computed: {
    isAmbassadorTheme () {
      return this.theme === 4;
    },
    isCampaignTheme () {
      return this.theme === 3;
    },
    title () {
      if (this.isAmbassadorTheme) {
        return `<span class="-bold">${this.model.Title}</span>&nbsp;<span class="-thin">${this.model.Label}</span>`;
      }
      return this.model.Title;
    },
    imgStyle () {
      if (this.loaded) {
        return {};
      }
      return {
        paddingBottom: this.imageObj.pbval
      };
    },
    imageObj () {
      if (this.model.ImageCollectorBlock) {
        const obj = this.model.ImageCollectorBlock.Media ? this.model.ImageCollectorBlock.Media[0] : {};
        const prop = this.isCampaignTheme ? null : this.proportion;
        return ImageCollectorHelper.buildMediaObject(obj, prop, this.sizes);
      } else if (this.model.Image) {
        const ext = this.model.Image.FilePath.split('.').pop();
        return {
          id: '',
          src: this.imgSrcBuilder(this.model.Image, this.model.Image.Width, this.model.Image.Height, 'transparent', { filetype: ext }),
          srcset: this.imgSrcsetBuilder(this.model.Image, 'transparent', 0, { filetype: ext }),
          description: this.model.Image.AlternateText || this.model.Image.Description || this.model.Image.Title || '',
          ratio: this.model.Image.Width / this.model.Image.Height,
          pbval: `${Math.floor((this.model.Image.Height / this.model.Image.Width) * 100)}%`
        };
      }
      return '';
    }
  }
};
</script>
<style
  lang="scss"
  src="@/scss/components/components.overlay-image.scss"
></style>
<style lang="scss">
@import "@/scss/defaults.scss";

.teaser-item {
  margin-bottom: 18px;
  @include media-breakpoint-up(md) {
    margin-bottom: 48px;
  }
  display: block;
  color: currentColor;
  h3 {
    margin-bottom: 6px;
  }
  p:last-child {
    margin-bottom: 0;
  }
  &:hover {
    text-decoration: none;
    color: currentColor;
  }
  .-text-on-image {
    .teaser-item-body {
      position: absolute;
      top: 18px;
      left: 18px;
      right: 18px;
      z-index: 10;
      color: $white;
      h3,
      .teaser-item-text p {
        @include font-responsive(larger);

        margin-bottom: 0;
      }
      h3 {
        font-weight: 500;
      }
      .teaser-item-text p {
        font-weight: 300;
      }
    }
  }
  &.-smalltext {
    p, a, div, span {
       @include font-size(small);
    }
    .tinymce-button a {
      height: 36px;
      line-height: 1rem !important
    }
  }

  img {
    width: 100%;
    margin-bottom: 21px;
  }

  h3,
  p {
    @include media-breakpoint-down(sm) {
      padding-left: 18px;
      padding-right: 18px;
    }

    .content-area & {
      @include media-breakpoint-down(sm) {
        padding-left: 0;
        padding-right: 0;
      }
    }
  }
  h3 {
    @include media-breakpoint-down(sm) {
      @include font-size(body);
    }
  }
  p {
    @include font-responsive(body);
    @include media-breakpoint-down(sm) {
      @include font-size(small);
    }
  }

  &.-campaign {
    margin-bottom: 18px;
    img {
      margin-bottom: 8px;
    }
    @include media-breakpoint-down(sm) {
      h3,
      .teaser-item-label,
      .teaser-item-text p {
        font-size: 11px;
        line-height: 15px;
        margin-bottom: 0;
      }

      .teaser-item-text.-thin p,
      .teaser-item-text.-thin p span {
        font-weight: 200;
      }
    }
  }
  &.-ambassador {
    img {
      border-radius: 50%;
    }
    h3 {
      @include font-responsive(body);
    }
    .teaser-item-label {
      display: none;
    }
    .teaser-item-text {
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
    .teaser-item-link,
    .teaser-item-text p {
      @include font-size(small);
    }
  }
}
a.teaser-item {
  .teaser-item-link {
    text-decoration: underline;
  }
}

</style>
